import {
  CardProps,
  Flex,
  IconButton,
  Skeleton,
  Spacer,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import React from "react";
import { RiDownloadLine } from "react-icons/ri";
import { GenericError } from "src/components/Feedback/GenericError";
import { Card } from "src/components/Layout/Card";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import * as RD from "src/types/remoteData";
import { EmptyChart } from "./EmptyChart";
import { chartToDataUrl, exportChart } from "./exportChart";

export const DATA_EXPORT_ID = "data-export-id";
const DATA_EXPORT_TITLE = "data-export-title";
const DATA_EXPORT_CHART = "data-export-chart";
const DATA_EXPORT_CAPTION = "data-export-caption";

type Row = { value: number | null };
type Props<T extends Row[]> = {
  title: string;
  data: RD.RemoteData<unknown, T>;
  filters: React.ReactNode;
  cardProps?: CardProps;
  children: (data: T) => React.ReactNode;
  caption?: string;
  aspectRatio: number;
  exportId?: string;
  exportFileName?: string;
};
export function DashboardChart<T extends Row[]>({
  title,
  data,
  filters,
  children,
  cardProps,
  caption,
  aspectRatio,
  exportId,
  exportFileName,
}: Props<T>) {
  const figureRef = React.useRef<HTMLDivElement>(null);
  const toast = useAvelaToast();
  const titleNode = (
    <Text
      fontSize="lg"
      fontWeight="semibold"
      {...{ [DATA_EXPORT_TITLE]: title }}
    >
      {title}
    </Text>
  );

  return (
    <Card
      {...{ [DATA_EXPORT_ID]: exportId }}
      showBorder
      display="flex"
      flexDirection="column"
      gap="5"
      {...cardProps}
    >
      <RemoteDataView
        remoteData={data}
        error={() => <ChartError title={titleNode} />}
        loading={<ChartLoading title={titleNode} />}
        config={{ showDataWhileReloading: false }}
      >
        {(data) => {
          const isEmpty = !data.some(
            (row) => row.value !== null && row.value > 0
          );
          return (
            <>
              <Flex gap="2" alignItems="center" wrap="wrap" width="full">
                {titleNode}
                <Spacer />
                <Flex
                  gap="2"
                  alignItems="center"
                  grow="1"
                  basis="18rem"
                  justifyContent="flex-end"
                >
                  {exportFileName && !isEmpty ? (
                    <Tooltip label="Download as PNG" hasArrow>
                      <IconButton
                        variant="ghost"
                        colorScheme="gray"
                        aria-label="Download as png"
                        icon={<RiDownloadLine />}
                        onClick={() => {
                          if (figureRef.current) {
                            exportChart(
                              figureRef.current,
                              exportFileName,
                              "png"
                            ).catch((err) => {
                              console.error("Failed to export chart:", err);
                              toast({
                                title: "Export failed",
                                description:
                                  "An error occurred while exporting the chart.",
                                status: "error",
                              });
                            });
                          }
                        }}
                      />
                    </Tooltip>
                  ) : null}
                  {filters}
                </Flex>
              </Flex>
              <Spacer />
              {isEmpty ? (
                <EmptyChart aspectRatio={aspectRatio} />
              ) : (
                <Flex
                  {...{ [DATA_EXPORT_CHART]: true }}
                  as="figure"
                  direction="column"
                  alignItems="center"
                  gap="2"
                  ref={figureRef}
                >
                  {children(data)}
                  {caption ? (
                    <Text
                      {...{ [DATA_EXPORT_CAPTION]: caption }}
                      as="figcaption"
                    >
                      {caption}
                    </Text>
                  ) : null}
                </Flex>
              )}
            </>
          );
        }}
      </RemoteDataView>
    </Card>
  );
}

export type ChartData = {
  title: string;
  dataUrl: string;
  caption: string;
};
export async function exportDashboardChart(
  dashboardChartElement: HTMLElement
): Promise<ChartData> {
  const figureEl = dashboardChartElement.querySelector(
    `[${DATA_EXPORT_CHART}]`
  );
  if (!figureEl) {
    throw new Error("No figure found");
  }
  const dataUrl = await chartToDataUrl(figureEl);
  const title =
    dashboardChartElement
      .querySelector<HTMLElement>(`[${DATA_EXPORT_TITLE}]`)
      ?.getAttribute(DATA_EXPORT_TITLE) ?? "";
  const caption =
    dashboardChartElement
      .querySelector<HTMLElement>(`[${DATA_EXPORT_CAPTION}]`)
      ?.getAttribute(DATA_EXPORT_CAPTION) ?? "";
  return {
    title,
    dataUrl,
    caption,
  };
}

const ChartLoading = (props: { title: React.ReactNode }) => {
  return (
    <Flex direction="column" gap="5">
      <Flex gap="2" alignItems="center" wrap="wrap" width="full">
        {props.title}
        <Spacer />
        <Flex gap="2" alignItems="center" grow="1">
          <Skeleton minW="5rem" height="2.5rem" flexGrow="1" />
          <Skeleton minW="5rem" height="2.5rem" flexGrow="1" />
        </Flex>
      </Flex>
      <Skeleton width="100%" height="26rem" />
    </Flex>
  );
};

const ChartError = (props: { title: React.ReactNode }) => {
  return (
    <Flex gap="2" alignItems="center" wrap="wrap" width="full">
      {props.title}
      <GenericError />
    </Flex>
  );
};
